import React from "react";
import { NavLink } from "react-router-dom";
import { NAV_GROUPS, ADMIN_ROLES } from "@/config/navigation";
import { useAuth } from "@/context/AuthContext";
import { cn } from "@/lib/utils";
import { ChevronsUpDown, Check } from "lucide-react";
import Logo from "@/components/brand/Logo";
import {
    DropdownMenu,
    DropdownMenuTrigger,
    DropdownMenuContent,
    DropdownMenuItem,
    DropdownMenuSeparator,
    DropdownMenuLabel,
} from "@/components/ui/dropdown-menu";

function WorkspaceSelector() {
    return (
        <DropdownMenu>
            <DropdownMenuTrigger
                data-testid="workspace-selector"
                className="group flex w-full items-center gap-2 rounded-md border border-zinc-200 bg-white px-2.5 py-2 text-left hover:bg-zinc-50 transition-colors"
            >
                <Logo size={26} />
                <div className="flex-1 min-w-0">
                    <div className="text-xs font-medium truncate">Yug Traders</div>
                    <div className="text-[10px] uppercase tracking-widest text-zinc-500">Primary</div>
                </div>
                <ChevronsUpDown className="h-3.5 w-3.5 text-zinc-400" strokeWidth={1.5} />
            </DropdownMenuTrigger>
            <DropdownMenuContent align="start" className="w-64">
                <DropdownMenuLabel className="text-xs uppercase tracking-widest text-zinc-500">
                    Workspaces
                </DropdownMenuLabel>
                <DropdownMenuItem className="justify-between">
                    <span>Yug Traders</span>
                    <Check className="h-3.5 w-3.5" strokeWidth={1.5} />
                </DropdownMenuItem>
                <DropdownMenuSeparator />
                <DropdownMenuItem disabled className="text-xs text-zinc-500">
                    Additional workspaces coming soon
                </DropdownMenuItem>
            </DropdownMenuContent>
        </DropdownMenu>
    );
}

export default function Sidebar() {
    const { user } = useAuth();
    const isAdmin = user && ADMIN_ROLES.includes(user.role);

    return (
        <aside
            data-testid="sidebar"
            className="flex h-screen w-64 flex-col border-r border-zinc-200 bg-zinc-50/60 backdrop-blur-sm"
        >
            <div className="flex items-center gap-2 px-4 h-14 border-b border-zinc-200">
                <Logo size={26} />
                <div className="font-display font-semibold text-sm tracking-tight">YugOS</div>
                <span className="ml-auto text-[10px] font-mono text-zinc-400">v0.1</span>
            </div>

            <div className="px-3 pt-3">
                <WorkspaceSelector />
            </div>

            <nav className="flex-1 overflow-y-auto px-2 py-4 space-y-6">
                {NAV_GROUPS.map((group) => {
                    const visible = group.items.filter((i) => !i.adminOnly || isAdmin);
                    if (visible.length === 0) return null;
                    return (
                        <div key={group.label}>
                            <div className="px-2 mb-1.5 text-[10px] font-semibold uppercase tracking-[0.18em] text-zinc-500">
                                {group.label}
                            </div>
                            <ul className="space-y-0.5">
                                {visible.map((item) => {
                                    const Icon = item.icon;
                                    return (
                                        <li key={item.id}>
                                            <NavLink
                                                to={item.path}
                                                data-testid={`nav-${item.id}`}
                                                className={({ isActive }) =>
                                                    cn(
                                                        "group flex items-center gap-2.5 rounded-md px-2 py-1.5 text-[13px] transition-colors",
                                                        isActive
                                                            ? "bg-zinc-900 text-white"
                                                            : "text-zinc-700 hover:bg-zinc-200/60 hover:text-zinc-950",
                                                    )
                                                }
                                            >
                                                <Icon
                                                    className={cn(
                                                        "h-[15px] w-[15px] shrink-0",
                                                        item.aiAccent && "text-blue-500",
                                                    )}
                                                    strokeWidth={1.5}
                                                />
                                                <span className="truncate">{item.label}</span>
                                                {item.aiAccent && (
                                                    <span className="ml-auto text-[9px] font-medium tracking-wider uppercase text-blue-500">
                                                        AI
                                                    </span>
                                                )}
                                            </NavLink>
                                        </li>
                                    );
                                })}
                            </ul>
                        </div>
                    );
                })}
            </nav>

            <div className="border-t border-zinc-200 px-3 py-3">
                <div className="text-[10px] text-zinc-500 font-mono">
                    Press{" "}
                    <kbd className="inline-flex h-4 items-center rounded border border-zinc-200 bg-white px-1 font-mono text-[10px] text-zinc-600">
                        ⌘K
                    </kbd>{" "}
                    to search
                </div>
            </div>
        </aside>
    );
}
